import { Header } from "./componentUtils/Header";
import {
  DataGrid,
  GridEnrichedColDef,
  GridSortModel,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@mui/styles";
import { Box, Stack, Typography } from "@mui/material";
import clsx from "clsx";
import axios from "../../../../services/axios";
import { ClassExtended, DbUser, InsightsFilterCtx } from "../../../../interfaces";
import { Donut } from "./componentUtils/Donut";
import { InsightsProgressFilterContext } from "../../../../context/InsightsProgressFilterContext";
import { StudentDetailedModal } from "./componentUtils/StudentDetailedModal";
import { FilterObj } from "../../../../types";
import { generateGtmEvent, HandleDataLayer, sanitizeData } from "../../../../Helpers";
import { CourseMetadata, SummaryType } from "@backend-types";
import { DownloadableReport } from "../../../../hooks/useXLSXGenerator";

const useStyles = makeStyles((theme) => ({
  dataTable: {
    borderRadius: "8px",
    border: `2px solid ${theme.palette.divider}`,
    backgroundColor: "white",
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      flexDirection: "row !important",
    },
    "& .MuiDataGrid-iconButtonContainer": {
      marginLeft: "auto",
    },
    "& .MuiDataGrid-columnHeaders": {
      height: "50px",
      minHeight: "unset",
      borderBottom: `2px solid ${theme.palette.divider}`,
      // border: "3px solid #E8E8E8 !important",
    },
    "& .MuiDataGrid-columnHeader": {
      "&:focus, &:focus-within": {
        outline: "none",
      },
    },
    "& .MuiDataGrid-columnHeader--sorted": {
      color: theme.palette.primary.main,
      "& .MuiDataGrid-sortIcon": {
        color: theme.palette.primary.main,
      },
    },
    "& .MuiDataGrid-cell": {
      border: "0px",
      "&:focus, &:focus-within": {
        outline: "none",
      },
    },
    "& .MuiDataGrid-row": {
      cursor: "pointer",
    },
  },
  dataTableHeaderWithBorder: {
    borderRight: `2px solid ${theme.palette.divider}`,
    "& > .MuiDataGrid-columnSeparator": {
      visibility: "hidden",
    },
  },
  dataTableCellWithBorder: {
    fontWeight: theme.typography.fontWeightMedium,
    borderRight: `2px solid ${theme.palette.divider} !important`,
  },
  dataTableHeader: {
    "& > .MuiDataGrid-columnSeparator": {
      visibility: "hidden",
    },
  },
  sortedDataTableValue: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
  },
  progressRoot: {
    backgroundColor: "#E8E8E8",
    position: "relative",
    overflow: "hidden",
    width: "100%",
    height: 8,
    borderRadius: 8,
  },
  progressBar: {
    height: "100%",
    "&.low": {
      backgroundColor: "#747474",
    },
    "&.medium": {
      backgroundColor: "#747474",
    },
    "&.high": {
      backgroundColor: "#479C2B",
    },
  },
}));

interface DetailedViewProps {
  classId: number;
  defaultModuleIds: string[];
  classDetails: ClassExtended;
  courseMetadata?: CourseMetadata;
}

export interface StudentSummary extends DbUser, Pick<DetailedViewProps, "classId"> {
  id: string;
  full_name: string;
  percentage: number;
  GPCorrect: number;
  GPAttempted: number;
  GPTotal: number;
  PPCorrect: number;
  PPAttempted: number;
  PPTotal: number;
  MPCorrect: number;
  MPAttempted: number;
  MPTotal: number;
  feedbackError: number;
  feedbackHint: number;
  feedbackErrorAvg: number;
  feedbackHintAvg: number;
  skillsTriggered: number;
  skillsViewed: number;
}

interface SelectedStudent extends StudentSummary, DetailedViewProps, DbUser {}

interface ProgressBarProps {
  value: number;
}

const getTotalCorrectProblem = (params: GridValueGetterParams) => {
  return params.row.PPCorrect + params.row.MPCorrect;
};

// const getTotalProblem = (params: GridValueGetterParams) => {
//   return params.row.PPTotal + params.row.MPTotal;
// };

const getTotalAttemptedProblem = (params: GridValueGetterParams) => {
  return params.row.PPAttempted + params.row.MPAttempted;
};

const getPercentCorrect = (params: GridValueGetterParams) => {
  let totalAttemptedProblems = getTotalAttemptedProblem(params);
  let totalCorrectProblems = getTotalCorrectProblem(params);
  if (totalAttemptedProblems + totalCorrectProblems === 0) {
    return 0;
  }
  return totalCorrectProblems / (totalCorrectProblems + totalAttemptedProblems);
};

const ProgressBar = React.memo(function ProgressBar(props: ProgressBarProps) {
  const { value } = props;
  const valueInPercent = value * 100;
  const classes = useStyles();

  return (
    <div className={classes.progressRoot}>
      <div
        className={clsx(classes.progressBar, {
          low: valueInPercent < 30,
          medium: valueInPercent >= 30 && valueInPercent <= 70,
          high: valueInPercent > 70,
        })}
        style={{ maxWidth: `${valueInPercent}%` }}
      />
    </div>
  );
});
const renderProgress = (params: any) => {
  return <ProgressBar value={params.value} />;
};

const renderDonut = (params: any) => {
  return (
    <Stack direction="row" alignItems="center" justifyContent="center">
      <Donut
        tiny={true}
        value={Math.round(params.value * 100)}
        label={"Label"}
        aria-label={"Label"}
      />
      <Typography variant="body1" sx={{ marginLeft: "12px" }}>
        {`${Math.round(params.value * 100)}%`}
      </Typography>
    </Stack>
  );
};

export const DetailedView = (props: DetailedViewProps) => {
  const classes = useStyles();
  const { classId, classDetails, courseMetadata } = props;
  const [summaryMap, setSummaryMap] = useState<Map<SummaryType, StudentSummary[]>>(
    new Map<SummaryType, StudentSummary[]>()
  );
  const [studentList, setStudentList] = React.useState<DbUser[]>([]);
  const [studentSummary, setStudentSummary] = React.useState<StudentSummary[]>([]);
  const [detailedDateFilter, setDetailedDateFilter] = useState<FilterObj>(
    {} as FilterObj
  );
  const [openStudentDetailModal, setOpenStudentDetailModal] = useState<boolean>(false);
  const [selectedStudent, setSelectedStudent] = useState<SelectedStudent>(
    {} as SelectedStudent
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState(100);

  const insightsProgressFilterCtx = useContext(
    InsightsProgressFilterContext
  ) as InsightsFilterCtx;
  const insightsProgressFilterCtxRef = useRef(insightsProgressFilterCtx);
  insightsProgressFilterCtxRef.current = insightsProgressFilterCtx;

  const updateSummaryMap = (key: SummaryType, value: StudentSummary[]) => {
    setSummaryMap((prevMap) => {
      const updatedMap = new Map(prevMap);
      updatedMap.set(key, value);
      return updatedMap;
    });
  };

  useEffect(() => {
    const summaryProgress = summaryMap.get("summary-progress");
    const summaryFeedback = summaryMap.get("summary-feedback");
    const newSummaryData = studentList.map((student) => {
      const studentSummaryProgress = summaryProgress?.find(
        (item) => item.id === student.id
      );
      const studentSummaryFeedback = summaryFeedback?.find(
        (item) => item.id === student.id
      );
      const returnSummary: StudentSummary = {
        ...student,
        classId: classId,
        percentage: studentSummaryProgress?.percentage ?? 0,
        GPCorrect: studentSummaryProgress?.GPCorrect ?? 0,
        GPAttempted: studentSummaryProgress?.GPAttempted ?? 0,
        GPTotal: studentSummaryProgress?.GPTotal ?? 0,
        PPCorrect: studentSummaryProgress?.PPCorrect ?? 0,
        PPAttempted: studentSummaryProgress?.PPAttempted ?? 0,
        PPTotal: studentSummaryProgress?.PPTotal ?? 0,
        MPCorrect: studentSummaryProgress?.MPCorrect ?? 0,
        MPAttempted: studentSummaryProgress?.MPAttempted ?? 0,
        MPTotal: studentSummaryProgress?.MPTotal ?? 0,
        feedbackError: studentSummaryFeedback?.feedbackError ?? 0,
        feedbackHint: studentSummaryFeedback?.feedbackHint ?? 0,
        feedbackErrorAvg: studentSummaryFeedback?.feedbackErrorAvg ?? 0,
        feedbackHintAvg: studentSummaryFeedback?.feedbackHintAvg ?? 0,
        skillsTriggered: studentSummaryFeedback?.skillsTriggered ?? 0,
        skillsViewed: studentSummaryFeedback?.skillsViewed ?? 0,
      };
      return returnSummary;
    });
    console.log("Refreshed cache state", newSummaryData);
    setStudentSummary(newSummaryData);
  }, [summaryMap, classId, studentList]);

  useEffect(() => {
    if (classId) {
      setLoading(true);
      const getStudentList = async () => {
        try {
          let response = await axios.get(`/insights/class/${classId}`);
          setStudentList(response.data);
        } catch (e) {
          console.log(e);
        }
      };
      getStudentList();
    }
  }, [classId]);

  useEffect(() => {
    setLoading(true);
    let courseFilter = insightsProgressFilterCtx.selectedCourseFilter ?? {
      moduleIds: props.defaultModuleIds,
    };
    if (!courseFilter) return;
    const getStudentSummary = async (type: SummaryType) => {
      let data = {
        type: type,
        students: studentList,
        courseFilter: courseFilter,
        startDate: detailedDateFilter.startDate,
        endDate: detailedDateFilter.endDate,
        classIds: [classId],
        v2Feedback: courseMetadata?.insights_page?.use_user_events_v2 ?? false,
      };
      try {
        let response = await axios.post(`/insights/student-summary`, data);
        const combined = studentList.map((studentDetail) => ({
          ...studentDetail,
          ...response.data.find((i: StudentSummary) => i.id === studentDetail.id),
          ...{ classId: classId },
        }));
        updateSummaryMap(type, combined);
        // setStudentSummary(combined);
        setLoading(false);
      } catch (e) {
        console.log(e);
      }
    };
    if (studentList.length > 0) {
      getStudentSummary("summary-progress").then(() => {
        getStudentSummary("summary-feedback");
      });
    } else {
      setLoading(false);
    }
  }, [
    studentList,
    detailedDateFilter,
    classId,
    props.defaultModuleIds,
    insightsProgressFilterCtx.selectedCourseFilter,
    courseMetadata?.insights_page?.use_user_events_v2,
  ]);

  const handleClick = (rowDetails: any) => {
    if (rowDetails) {
      setSelectedStudent(rowDetails);
      setOpenStudentDetailModal(true);
    }
  };

  const columns: GridEnrichedColDef[] = [
    {
      field: "full_name",
      headerName: "Student",
      type: "string",
      align: "left",
      minWidth: 368,
      headerClassName: classes.dataTableHeaderWithBorder,
      cellClassName: classes.dataTableCellWithBorder,
    },
    {
      field: "percentage",
      headerName: "Progress",
      renderCell: renderProgress,
      align: "left",
      minWidth: 220,
      headerClassName: classes.dataTableHeader,
    },
    {
      field: "GPCorrect",
      hide: true,
    },
    {
      field: "GPAttempted",
      hide: true,
    },
    {
      field: "GPTotal",
      hide: true,
    },
    {
      field: "PPCorrect",
      hide: true,
    },
    {
      field: "PPAttempted",
      hide: true,
    },
    {
      field: "PPTotal",
      hide: true,
    },
    {
      field: "MPCorrect",
      hide: true,
    },
    {
      field: "MPAttempted",
      hide: true,
    },
    {
      field: "MPTotal",
      hide: true,
    },
    {
      field: "totalProblems",
      headerName: "Percent correct",
      type: "number",
      align: "left",
      minWidth: 220,
      headerClassName: classes.dataTableHeader,
      renderCell: renderDonut,
      valueGetter: getPercentCorrect,
    },
  ];
  if (!courseMetadata?.insights_page?.disable_feedback_data) {
    columns.push(
      {
        field: "skillsTriggered",
        headerName: "Skill videos triggered by errors",
        type: "number",
        align: "left",
        minWidth: 220,
        headerClassName: classes.dataTableHeader,
      },
      {
        field: "skillsViewed",
        headerName: "Number of Skill videos viewed",
        type: "number",
        align: "left",
        minWidth: 220,
        headerClassName: classes.dataTableHeader,
      },
      {
        field: "feedbackErrorAvg",
        headerName: "Average errors produced",
        type: "number",
        align: "left",
        minWidth: 220,
        headerClassName: classes.dataTableHeader,
      },
      {
        field: "feedbackHintAvg",
        headerName: "Average hints per practice problem",
        type: "number",
        align: "left",
        minWidth: 220,
        headerClassName: classes.dataTableHeader,
      }
    );
  } else {
    // columns.push(
    //   {
    //     field: "skillsTriggered",
    //     headerName: "Skill videos triggered by errors",
    //     type: "number",
    //     align: "left",
    //     minWidth: 220,
    //     headerClassName: classes.dataTableHeader,
    //   },
    //   {
    //     field: "skillsViewed",
    //     headerName: "Number of Skill videos viewed",
    //     type: "number",
    //     align: "left",
    //     minWidth: 220,
    //     headerClassName: classes.dataTableHeader,
    //   }
    // );
  }

  const classDetailsReport = useMemo<DownloadableReport>(() => {
    const modifiedData = studentSummary.map((summaryData) => {
      let totalAttemptedProblems = summaryData.PPAttempted + summaryData.MPAttempted;
      let totalCorrectProblems = summaryData.PPCorrect + summaryData.MPCorrect;
      const data: any = {
        Student: summaryData.full_name,
        "Percent correct":
          totalAttemptedProblems + totalCorrectProblems === 0
            ? 0
            : Math.round(
                (totalCorrectProblems / (totalCorrectProblems + totalAttemptedProblems)) *
                  100
              ),
      };
      if (!courseMetadata?.insights_page?.disable_feedback_data) {
        data["Skill videos triggered by errors"] = summaryData.skillsTriggered;
        data["Number of Skill videos viewed"] = summaryData.skillsViewed;
        data["Average errors produced"] = summaryData.feedbackErrorAvg;
        data["Average hints per practice problem"] = summaryData.feedbackHintAvg;
      }
      return sanitizeData(data);
    });

    return {
      fileName: `${classDetails.subject} ${classDetails.period_block} - Student Overall Progress`,
      sheets: [
        {
          name: "Student Overall Progress",
          header: ["heading1"],
          data: modifiedData,
        },
      ],
    };
  }, [
    classDetails.subject,
    classDetails.period_block,
    studentSummary,
    courseMetadata?.insights_page?.disable_feedback_data,
  ]);

  return (
    <>
      <Header
        title="Student overall progress"
        onDateFilterChange={(filterObj) => {
          setDetailedDateFilter(filterObj);
        }}
        downloadableReport={classDetailsReport}
      />
      <Box style={{ width: "100%", marginTop: "32px" }}>
        <DataGrid
          className={classes.dataTable}
          rows={studentSummary}
          // onCellClick={(e) => handleClick(e.row, e.field)}
          onRowClick={(e) => {
            HandleDataLayer(
              "modal",
              "open",
              "click",
              "main content",
              "student overall progress",
              e.row.full_name,
              "onclick"
            );
            handleClick(e.row);
          }}
          onSortModelChange={(model: GridSortModel) => {
            const colDef = columns.find((item) => item.field === model[0].field);
            const headerName = colDef?.headerName;
            generateGtmEvent({
              event: "link",
              action: "click",
              name: "onclick",
              type: "internal link",
              region: "main content",
              section: "student overall progress",
              text: `${headerName ?? model[0].field} ^ sort by`,
            });
          }}
          paginationMode="client"
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => {
            HandleDataLayer(
              "collapse",
              "open",
              "click",
              "main content",
              "rows per page",
              pageSize.toString(),
              "onclick",
              "student overall progress"
            );
            HandleDataLayer(
              "link",
              "click",
              "internal link",
              "main content",
              "rows per page",
              newPageSize.toString(),
              "onclick",
              "student overall progress"
            );
            setPageSize(newPageSize);
          }}
          rowsPerPageOptions={[25, 50, 100]}
          pagination
          columns={columns}
          autoHeight
          disableSelectionOnClick
          disableColumnSelector
          disableDensitySelector
          disableColumnMenu
          loading={loading}
        />
      </Box>
      <StudentDetailedModal
        classId={selectedStudent.classId}
        handleClose={() => {
          setOpenStudentDetailModal(false);
        }}
        defaultModuleIds={props.defaultModuleIds}
        open={openStudentDetailModal}
        studentName={selectedStudent.full_name}
        progress={selectedStudent.percentage}
        studentEmail={selectedStudent.email}
        studentId={selectedStudent.id}
        classDetails={classDetails}
        courseMetadata={courseMetadata}
      />
    </>
  );
};
